import { Controller } from "@hotwired/stimulus"

// Custom Location TreeSelect for Philippines hierarchical location selection
// Connects to data-controller="location-tree-select"
// Supports two modes:
// - 'strings' (default): stores "City, Province" format (legacy)
// - 'ids': stores city IDs as JSON array (new normalized format)
export default class extends Controller {
  static targets = ["container", "hiddenInput", "hiddenInputIds", "searchInput", "summary"]
  static values = {
    data: Array,
    selected: Array,
    selectedIds: Array,
    mode: { type: String, default: 'strings' } // 'strings' for legacy, 'ids' for new
  }

  connect() {
    // Initialize selections based on mode
    // In 'ids' mode, selectedCityIds is the single source of truth
    // In 'strings' mode, selections is the source of truth
    if (this.modeValue === 'ids') {
      this.selectedCityIds = new Set(this.selectedIdsValue || [])
    } else {
      this.selections = new Set(this.selectedValue || [])
    }

    this.expandedNodes = new Set()
    this.searchQuery = ''

    this.renderTree()

    this.updateSummary()
    this.updateHiddenField()
  }

  // Derive display strings from IDs - single source of truth approach
  getSelectionsForDisplay() {
    if (this.modeValue === 'ids') {
      const displayStrings = new Set()
      this.selectedCityIds.forEach(cityId => {
        const cityNode = this.findCityNodeById(this.dataValue, cityId)
        if (cityNode && cityNode.provinceName) {
          displayStrings.add(`${cityNode.name}, ${cityNode.provinceName}`)
        }
      })
      return displayStrings
    }
    return this.selections
  }

  findCityNodeById(nodes, targetId) {
    for (let node of nodes) {
      if (node.value === `city_${targetId}`) {
        return node
      }
      if (node.children) {
        const found = this.findCityNodeById(node.children, targetId)
        if (found) return found
      }
    }
    return null
  }

  disconnect() {
  }

  renderTree() {
    if (!this.dataValue || !this.dataValue.length) {
      console.warn('No tree data available')
      return
    }

    const treeHtml = this.generateTreeHTML(this.dataValue)
    this.containerTarget.innerHTML = treeHtml
    
    // Attach event listeners
    this.attachEventListeners()
  }

  generateTreeHTML(nodes, level = 0) {
    return nodes.filter(node => this.shouldShowNode(node, this.searchQuery))
                .map(node => {
      const hasChildren = node.children && node.children.length > 0
      const isCity = node.value && node.value.startsWith('city_')
      const isExpanded = this.expandedNodes.has(node.value)
      
      // Create proper indentation based on tree level
      const indentPixels = level * 24  // 24px per level
      const indentStyle = `padding-left: ${indentPixels}px;`
      
      // Only cities can be selected - check if city is in selections
      let isSelected = false
      if (isCity && node.provinceName) {
        if (this.modeValue === 'ids') {
          // Check by city ID
          const cityId = parseInt(node.value.replace('city_', ''))
          isSelected = this.selectedCityIds.has(cityId)
        } else {
          // Check by "City, Province" string
          const cityWithProvince = `${node.name}, ${node.provinceName}`
          isSelected = this.selections.has(cityWithProvince)
        }
      }

      let html = `
        <div class="tree-node flex items-center py-2 px-1 ${isCity ? 'hover:bg-teal-50' : 'hover:bg-gray-50'} rounded-sm transition-colors duration-150" 
             data-value="${node.value}" 
             data-level="${level}"
             style="${indentStyle}">
      `

      // Expand/collapse button for parent nodes
      if (hasChildren) {
        const expandIcon = isExpanded ? '▼' : '▶'
        const expandClass = isExpanded ? 'text-teal-600' : 'text-gray-400'
        html += `
          <button class="expand-btn w-4 h-4 mr-3 ${expandClass} hover:text-teal-700 cursor-pointer transition-colors duration-150"
                  data-action="click->location-tree-select#toggleNode" 
                  data-node-value="${node.value}">
            ${expandIcon}
          </button>
        `
      } else {
        html += `<span class="w-4 h-4 mr-3"></span>` // Spacer for alignment
      }

      // Checkbox - only show for cities
      if (isCity) {
        const checkedAttr = isSelected ? 'checked' : ''
        html += `
          <input type="checkbox" 
                 class="tree-checkbox mr-3 rounded border-gray-300 text-teal-600 focus:ring-teal-500 focus:ring-offset-0" 
                 value="${node.value}"
                 data-action="change->location-tree-select#selectLocation"
                 ${checkedAttr}>
        `
      } else {
        html += `<span class="w-4 h-4 mr-3"></span>` // Spacer for non-selectable items
      }

      // Label with icon and search highlighting
      const icon = this.getLocationIcon(node.value, level)
      const highlightedName = this.highlightSearchText(node.displayName || node.name, this.searchQuery)
      
      // Style cities as selectable, others as navigation
      let labelClass
      if (isCity) {
        labelClass = isSelected 
          ? "tree-label text-sm text-navy-800 font-medium cursor-pointer flex" 
          : "tree-label text-sm text-navy-700 cursor-pointer flex"
      } else {
        // Non-selectable navigation items
        labelClass = "tree-label text-sm text-gray-600 flex font-medium"
      }
      
      html += `
        <div class="${labelClass}" data-node-value="${node.value}">
          <span class="mr-2 w-4 flex items-center justify-center">${icon}</span>
          ${highlightedName}
        </div>
      `

      html += `</div>`

      // Children (if expanded)  
      if (hasChildren && isExpanded) {
        html += `<div class="children" data-parent="${node.value}">
            ${this.generateTreeHTML(node.children, level + 1)}
          </div>`
      }

      return html
    }).join('')
  }

  getAllChildValues(node) {
    let values = []
    if (node.children) {
      node.children.forEach(child => {
        values.push(child.value)
        values = values.concat(this.getAllChildValues(child))
      })
    }
    return values
  }

  getLocationIcon(value, level) {
    if (value && value.startsWith('region_')) return '<i class="ph-fill ph-map-pin text-teal-600"></i>'
    if (value && value.startsWith('province_')) return '<i class="ph-fill ph-map-trifold text-navy-600"></i>'
    if (value && value.startsWith('city_')) return '<i class="ph-fill ph-buildings text-blue-600"></i>'
    return '<i class="ph-fill ph-map-pin text-gray-500"></i>'
  }

  attachEventListeners() {
    // Event listeners are handled by Stimulus data-action attributes
    // No additional setup needed for city-only selection
  }

  findNodeByValue(nodes, targetValue) {
    for (let node of nodes) {
      if (node.value === targetValue) {
        return node
      }
      if (node.children) {
        const found = this.findNodeByValue(node.children, targetValue)
        if (found) return found
      }
    }
    return null
  }

  toggleNode(event) {
    const nodeValue = event.target.dataset.nodeValue
    
    if (this.expandedNodes.has(nodeValue)) {
      this.expandedNodes.delete(nodeValue)
    } else {
      this.expandedNodes.add(nodeValue)
    }
    
    // Re-render tree
    this.renderTree()
  }

  selectLocation(event) {
    const checkbox = event.target
    const cityValue = checkbox.value
    const isChecked = checkbox.checked

    // Only process city selections
    if (!cityValue.startsWith('city_')) {
      checkbox.checked = false // Prevent selection of non-cities
      return
    }

    // Extract city ID for ids mode
    const cityId = parseInt(cityValue.replace('city_', ''))

    // Find the city node to get province name
    const cityNode = this.findNodeByValue(this.dataValue, cityValue)
    if (!cityNode || !cityNode.provinceName) {
      console.error('City node or province name not found for:', cityValue)
      checkbox.checked = false
      return
    }

    const cityWithProvince = `${cityNode.name}, ${cityNode.provinceName}`

    // Check current selection count based on mode
    const currentCount = this.modeValue === 'ids'
      ? this.selectedCityIds.size
      : this.selections.size

    if (isChecked) {
      // Check if we're at the 3-city limit
      if (currentCount >= 3) {
        checkbox.checked = false
        this.showLimitMessage()
        return
      }

      // Add to the appropriate source of truth based on mode
      if (this.modeValue === 'ids') {
        this.selectedCityIds.add(cityId)
      } else {
        this.selections.add(cityWithProvince)
      }
    } else {
      // Remove from the appropriate source of truth based on mode
      if (this.modeValue === 'ids') {
        this.selectedCityIds.delete(cityId)
      } else {
        this.selections.delete(cityWithProvince)
      }
    }

    // Update UI and form
    this.updateSummary()
    this.updateHiddenField()
  }

  showLimitMessage() {
    console.log('3-city limit reached')
    // You could add a temporary tooltip or flash message here
  }


  updateSummary() {
    if (!this.hasSummaryTarget) return

    const selections = this.getSelectionsForDisplay()
    const count = selections.size
    let summaryHTML = ''
    let iconClass = 'ph-fill ph-buildings text-teal-600 mr-2'

    if (count === 0) {
      summaryHTML = `
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="${iconClass}"></i>
            <span class="text-sm font-medium text-teal-800">No cities selected</span>
          </div>
          <span class="text-xs text-gray-500">0/3 cities</span>
        </div>
      `
    } else {
      const cityList = Array.from(selections)
      const displayText = count === 1 
        ? `${count} city selected` 
        : `${count} cities selected`
      
      const limitClass = count >= 3 ? 'text-coral-600 bg-coral-100' : 'text-teal-600 bg-teal-100'
      
      summaryHTML = `
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="${iconClass}"></i>
            <span class="text-sm font-medium text-teal-800">${displayText}</span>
          </div>
          <span class="text-xs ${limitClass} px-2 py-1 rounded-full">${count}/3 cities</span>
        </div>
        <div class="mt-2 text-xs text-gray-600">
          ${cityList.join(' • ')}
        </div>
      `
    }

    this.summaryTarget.innerHTML = summaryHTML
  }

  updateHiddenField() {
    if (this.modeValue === 'ids') {
      // New mode: store city IDs as JSON array in hiddenInputIds
      if (this.hasHiddenInputIdsTarget) {
        const idsArray = Array.from(this.selectedCityIds)
        this.hiddenInputIdsTarget.value = JSON.stringify(idsArray)
        this.hiddenInputIdsTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }

      // Also update legacy field for backward compatibility during transition
      // Derive strings on-demand from the single source of truth (selectedCityIds)
      if (this.hasHiddenInputTarget) {
        const selectionsArray = Array.from(this.getSelectionsForDisplay())
        this.hiddenInputTarget.value = selectionsArray.join('|')
        this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }
    } else {
      // Legacy mode: store as pipe-separated strings
      if (!this.hasHiddenInputTarget) return

      const selectionsArray = Array.from(this.selections)

      // Store as pipe-separated values to preserve "City, Province" format
      this.hiddenInputTarget.value = selectionsArray.join('|')

      // Trigger change event for Rails form handling
      this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // Search functionality
  search(event) {
    const query = event.target.value.toLowerCase().trim()
    
    this.searchQuery = query
    
    if (query === '') {
      // Clear search - collapse all nodes and show normal tree
      this.expandedNodes.clear()
    } else {
      // Expand nodes that contain matches
      this.expandNodesWithMatches(this.dataValue, query)
    }
    
    // Re-render tree with search highlighting
    this.renderTree()
  }

  expandNodesWithMatches(nodes, query) {
    nodes.forEach(node => {
      const hasMatch = node.name.toLowerCase().includes(query)
      const hasChildMatch = this.hasMatchInChildren(node, query)
      
      if (hasMatch || hasChildMatch) {
        // Expand this node if it or its children have matches
        this.expandedNodes.add(node.value)
      }
      
      if (node.children) {
        this.expandNodesWithMatches(node.children, query)
      }
    })
  }

  hasMatchInChildren(node, query) {
    if (!node.children) return false
    
    return node.children.some(child => {
      return child.name.toLowerCase().includes(query) || 
             this.hasMatchInChildren(child, query)
    })
  }

  shouldShowNode(node, query) {
    if (query === '') return true
    
    // Show node if it matches search
    if (node.name.toLowerCase().includes(query)) return true
    
    // Show node if any of its children match (for parent context)
    return this.hasMatchInChildren(node, query)
  }

  highlightSearchText(text, query) {
    if (!query || query === '') return text
    
    const regex = new RegExp(`(${query})`, 'gi')
    return text.replace(regex, '<mark class="bg-coral-100 text-coral-900 px-1 rounded">$1</mark>')
  }

  // Clear all selections
  clearSelections() {
    // Clear the appropriate source of truth based on mode
    if (this.modeValue === 'ids') {
      this.selectedCityIds.clear()
    } else {
      this.selections.clear()
    }
    this.renderTree()
    this.updateSummary()
    this.updateHiddenField()
  }
}